/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2022 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2022 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { AxiosError } from 'axios';
import type { TFunction } from 'i18next';
import {
  dateUtils,
  getErrorMessage,
  getWarningMessage,
} from '@opengeoweb/shared';
import type {
  AutoTAFlocations,
  Taf,
  TafConfig,
  TafConfigLocation,
  TafConfigLocations,
  TafConfigLocationFixedPeriod,
  TafConfigLocationDynamicPeriod,
  TafFromBackend,
  TafFromFrontEnd,
  TafStatus,
  TimeSlot,
} from '../../types';
import { DATE_FORMAT_BASE_TIME } from '../../utils/dateFormats';

export const isTafConfigLocationFixedPeriod = (
  location: TafConfigLocation,
): location is TafConfigLocationFixedPeriod => {
  return (
    (location as TafConfigLocationFixedPeriod).activity_period !== undefined
  );
};

export const isTafConfigLocationDynamicPeriod = (
  location: TafConfigLocation,
): location is TafConfigLocationDynamicPeriod => {
  return (
    (location as TafConfigLocationDynamicPeriod).valid_durations !==
      undefined &&
    (location as TafConfigLocationDynamicPeriod).valid_start_times !== undefined
  );
};

export const getLocationSetting = (
  location: string,
  locationSettings: TafConfigLocations,
): TafConfigLocation => {
  return locationSettings[location];
};

export const sortTafsOnLocation = (
  tafList: TafFromBackend[] = [],
  locationOrder: string[] = [],
): TafFromBackend[] => {
  return [...tafList].sort(({ taf: tafA }, { taf: tafB }) =>
    locationOrder.indexOf(tafA.location) < locationOrder.indexOf(tafB.location)
      ? -1
      : 1,
  );
};

export const sortTafsOnIssueDate = (
  tafList: TafFromBackend[] = [],
): TafFromBackend[] => {
  return [...tafList].sort(({ taf: tafA }, { taf: tafB }) => {
    if (
      tafA.issueDate! < tafB.issueDate! ||
      (tafA.issueDate && !tafB.issueDate)
    ) {
      return 1;
    }
    return -1;
  });
};

export const getBaseTime = (
  baseTime: string,
  location: string,
  tafConfig: TafConfig,
): {
  currentBaseTime: Date;
  nextBaseTime: Date;
} => {
  const configLocation = getLocationSetting(location, tafConfig.locations);

  let currentBaseTime: Date;
  let nextBaseTime: Date;

  if (isTafConfigLocationFixedPeriod(configLocation)) {
    const activityPeriod = configLocation.activity_period;
    currentBaseTime = dateUtils.utc(baseTime);
    nextBaseTime = dateUtils.add(dateUtils.utc(baseTime), {
      hours: activityPeriod,
    });
  } else {
    currentBaseTime = dateUtils.utc(baseTime);
    const startTimeIndex = configLocation.valid_start_times.findLastIndex(
      (hour) => currentBaseTime.getUTCHours() >= hour,
    );

    // when current hour is lower than then first item in valid_start_times. we need to move to the last hour of the previous day
    if (startTimeIndex === -1) {
      const lastStartTimeIndex = configLocation.valid_start_times.length - 1;
      currentBaseTime = dateUtils.sub(currentBaseTime, {
        days: 1,
      });
      currentBaseTime = dateUtils.set(currentBaseTime, {
        hours: configLocation.valid_start_times[lastStartTimeIndex],
        minutes: 0,
        seconds: 0,
      });
    } else {
      // We make sure that the hours match the value in valid_start_times
      // or should we just assume that the baseTime aligns with the config here?
      currentBaseTime = dateUtils.set(currentBaseTime, {
        hours: configLocation.valid_start_times[startTimeIndex],
        minutes: 0,
        seconds: 0,
      });
    }

    const nextStartTimeIndex =
      (startTimeIndex + 1) % configLocation.valid_start_times.length;
    const nextStartTimeHour =
      configLocation.valid_start_times[nextStartTimeIndex];

    nextBaseTime = dateUtils.set(currentBaseTime, {
      hours: nextStartTimeHour,
      minutes: 0,
      seconds: 0,
    });

    // If next start time is earlier than or equal to current start time, it means we've wrapped to the next day
    if (nextStartTimeHour <= configLocation.valid_start_times[startTimeIndex]) {
      nextBaseTime = dateUtils.add(nextBaseTime, { days: 1 });
    }
  }

  return { currentBaseTime, nextBaseTime };
};

/**
 * If we don't have a taf to get the baseTime from, we calculate it based on the current time and the activity_period from the config.
 * @param tafConfig
 * @returns
 */
export const calculateBaseTime = (
  tafConfig: TafConfig,
): {
  currentBaseTime: Date;
  nextBaseTime: Date;
} => {
  const currentTime = dateUtils.utc();
  const currentHour = dateUtils.getHours(currentTime);
  // take the first location from config (via locationOrder)
  const firstLocation = tafConfig.locationOrder[0];

  const configLocation = getLocationSetting(firstLocation, tafConfig.locations);

  if (isTafConfigLocationFixedPeriod(configLocation)) {
    const activityPeriod = configLocation.activity_period;

    const baseHour = Math.floor(currentHour / activityPeriod) * activityPeriod;

    const currentBaseTime = dateUtils.set(currentTime, {
      hours: baseHour,
      minutes: 0,
      seconds: 0,
      milliseconds: 0,
    });

    const nextBaseTime = dateUtils.add(currentBaseTime, {
      hours: activityPeriod,
    });

    return { currentBaseTime, nextBaseTime };
  }
  return getBaseTime(currentTime.toUTCString(), firstLocation, tafConfig);
};

export const getTafTimeSlot = (
  baseTime: string,
  location: string,
  status: TafStatus,
  tafConfig: TafConfig,
): TimeSlot => {
  const now = dateUtils.utc();
  const { currentBaseTime, nextBaseTime } = getBaseTime(
    baseTime,
    location,
    tafConfig,
  );

  if (status === 'EXPIRED') {
    return 'EXPIRED';
  }
  if (
    currentBaseTime.getTime() < now.getTime() &&
    nextBaseTime.getTime() > now.getTime()
  ) {
    return 'ACTIVE';
  }
  return 'UPCOMING';
};

interface TafTimeSlots {
  current: TafFromBackend[];
  upcoming: TafFromBackend[];
  expired: TafFromBackend[];
}

export const sortTafTimeSlots = (
  tafList: TafFromBackend[],
  tafConfig: TafConfig,
): TafTimeSlots => {
  const sortedListOnIssueDate = sortTafsOnIssueDate(tafList);
  const sortedListOnLocation = sortTafsOnLocation(
    sortedListOnIssueDate,
    tafConfig.locationOrder,
  );

  return sortedListOnLocation.reduce<TafTimeSlots>(
    (sortedTafList, tafFromBackend) => {
      const { taf } = tafFromBackend;

      const timeSlot = getTafTimeSlot(
        taf.baseTime,
        taf.location,
        taf.status,
        tafConfig,
      );
      if (timeSlot === 'EXPIRED') {
        sortedTafList.expired.push(tafFromBackend);
      } else if (timeSlot === 'ACTIVE') {
        sortedTafList.current.push(tafFromBackend);
      } else {
        sortedTafList.upcoming.push(tafFromBackend);
      }

      return sortedTafList;
    },
    { current: [], upcoming: [], expired: [] },
  );
};

export const muiTabA11yProps = (
  index: number,
): { id: string; 'aria-controls': string } => {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
};

export const getNewTafLocationIndex = (
  tafLocations: TafFromBackend[],
  previousTafLocationUUID: string,
): number => {
  const newActiveIndexForDraft = tafLocations.findIndex(
    (location) => location?.taf?.previousId === previousTafLocationUUID,
  );
  if (newActiveIndexForDraft !== -1) {
    return newActiveIndexForDraft;
  }

  const newActiveIndexForPublished = tafLocations.findIndex(
    (location) => location?.taf?.uuid === previousTafLocationUUID,
  );
  if (newActiveIndexForPublished !== -1) {
    return newActiveIndexForPublished;
  }

  return 0;
};

export const getPreviousTaf = (
  tafList: TafFromBackend[],
  currentTaf: TafFromBackend,
): Taf => {
  const { previousId } = currentTaf.taf;
  if (!previousId) {
    return null!;
  }
  const previousTaf = tafList.find((taf) => taf.taf.uuid === previousId);
  return previousTaf ? previousTaf.taf : null!;
};

export const getAutoTafLocations = (
  autoTAFlocations: AutoTAFlocations | undefined,
): AutoTAFlocations | null => {
  return autoTAFlocations || null;
};

export const scrollToPosition = (
  scrollRef: React.RefObject<HTMLElement>,
  activeLocation: string,
): void => {
  if (!scrollRef.current) {
    return;
  }
  const containerTop = scrollRef.current.getBoundingClientRect().top;
  const firstLocation = scrollRef.current.querySelector(
    `[data-location="${activeLocation}"]`,
  );

  const paddingTop = parseInt(
    getComputedStyle(scrollRef.current).paddingTop,
    10,
  );
  const firstLocationTop = firstLocation?.getBoundingClientRect().top!;
  const newScrollPosition =
    firstLocationTop - containerTop + scrollRef.current.scrollTop - paddingTop;

  scrollRef.current.scrollTo(newScrollPosition, newScrollPosition);
};

export const formatBaseTime = (time: Date): string =>
  dateUtils.dateToString(time, DATE_FORMAT_BASE_TIME)!;

const getTafMessageContent = ({
  location,
  validDateStart,
  messageType,
}: Taf): { location: string; formattedDate: string; messageType: string } => {
  const formattedDate = formatBaseTime(dateUtils.utc(validDateStart));
  return {
    location,
    formattedDate,
    messageType,
  };
};

export const getSnackbarMessage = (
  tafFromFrontEnd: TafFromFrontEnd,
  isAutoSaved: boolean,
  t: TFunction,
): string => {
  const { changeStatusTo, taf } = tafFromFrontEnd;
  if (isAutoSaved) {
    return t('snackbar-auto-save');
  }

  const content = getTafMessageContent(taf);

  switch (changeStatusTo) {
    case 'DRAFT':
      return t('snackbar-draft');
    case 'DRAFT_AMENDED':
      return t('snackbar-draft-amend');
    case 'DRAFT_CORRECTED':
      return t('snackbar-draft-correct');
    case 'PUBLISHED':
      return t('snackbar-publish', content);
    case 'CANCELLED':
      return t('snackbar-cancel', content);
    case 'CORRECTED':
      return t('snackbar-correct', content);
    case 'AMENDED':
      return t('snackbar-amend', content);
    default:
      return t('snackbar-save', content);
  }
};

export interface TafError {
  type: 'CANCELLED' | 'SERVER' | 'WARNING';
  title: string;
  message?: string;
  testId?: string;
}

export const isWarning = (error: TafError): boolean =>
  error?.type === 'WARNING';

export const parseErrorMessage = (
  error: AxiosError | Error,
  params: TafFromFrontEnd,
  t: TFunction,
): TafError => {
  const message = getErrorMessage(error);
  const warningMessage = getWarningMessage(error);

  if (warningMessage) {
    return {
      type: 'WARNING',
      title: warningMessage,
    };
  }

  return params.changeStatusTo === 'CANCELLED'
    ? {
        type: 'CANCELLED',
        title: t('error-cancel'),
        message,
      }
    : {
        type: 'SERVER',
        title: t('error-save'),
        message,
      };
};
